/**
 * Contract Hooks - React hooks for contract data management
 * Epic 26: Szerződés kezelés
 */

import {
  createContract,
  deleteContract,
  getContractById,
  getContracts,
  updateContract,
  type Contract,
  type ContractFilters,
  type ContractListResponse,
} from '@/api/contracts';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';

// ============================================
// TYPES
// ============================================

interface UseContractsResult {
  contracts: Contract[];
  total: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseContractResult {
  contract: Contract | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseContractMutationsResult {
  createContract: typeof createContract;
  updateContract: typeof updateContract;
  deleteContract: typeof deleteContract;
  isLoading: boolean;
  error: string | null;
}

// ============================================
// HOOKS
// ============================================

export function useContracts(filters: ContractFilters = {}): UseContractsResult {
  const { t } = useTranslation();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContracts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response: ContractListResponse = await getContracts(filters);
      setContracts(response.data);
      setTotal(response.meta.total);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.contracts.loadAll');
      setError(message);
      console.error('Error fetching contracts:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters.search, filters.status, filters.type, filters.page, filters.pageSize]);

  useEffect(() => {
    void fetchContracts();
  }, [fetchContracts]);

  return { contracts, total, isLoading, error, refetch: fetchContracts };
}

export function useContract(id: string | undefined): UseContractResult {
  const { t } = useTranslation();
  const [contract, setContract] = useState<Contract | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContract = useCallback(async () => {
    if (!id) {
      setContract(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getContractById(id);
      setContract(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.contracts.loadOne');
      setError(message);
      console.error('Error fetching contract:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchContract();
  }, [fetchContract]);

  return { contract, isLoading, error, refetch: fetchContract };
}

export function useContractMutations(): UseContractMutationsResult {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wrap =
    <T extends unknown[], R>(fn: (...args: T) => Promise<R>) =>
    async (...args: T): Promise<R> => {
      setIsLoading(true);
      setError(null);
      try {
        return await fn(...args);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.common.operationFailed');
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

  return {
    createContract: wrap(createContract),
    updateContract: wrap(updateContract),
    deleteContract: wrap(deleteContract),
    isLoading,
    error,
  };
}
